import {
    GetPayrollClientCompItemConfigurationQuery,
    GetPayrollEmployeeDataQuery,
    useGetPayrollClientCompItemConfigurationLazyQuery,
    useGetPayrollEmployeeDataLazyQuery
} from "../../__generated__/generated_types";
import {ApolloError} from "@apollo/client";
import {useEffect, useState} from "react";
import {Property} from "../../contexts/properties/logic";
import {transposeAcquisitionDateDatabaseToDisplay} from "../workflows/account/formula-bar/logic/utils";

export type PayrollQueryData = GetPayrollEmployeeDataQuery["listEmployees"]["items"];
type PayrollCompItemConfig = Omit<GetPayrollClientCompItemConfigurationQuery["getPayrollClientCompItemConfiguration"], "__typename">;

export interface PayrollEmployeeDataState {
    payrollEmployeeData: PayrollQueryData | undefined;
    payrollEmployeeDataLoading: boolean;
    payrollEmployeeDataError:ApolloError | undefined;
    payrollEmployeeDataRefetch: () => void;
    payrollCompItemConfig: PayrollCompItemConfig;
}

export function usePayrollEmployeeData(props: {budgetYear: number | undefined, currentProperty: Property | undefined}): PayrollEmployeeDataState {
    const [data, setData] = useState<PayrollQueryData | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<ApolloError | undefined>(undefined);
    const [partialData, setPartialData] = useState<PayrollQueryData>([]);
    const [compItemConfig, setCompItemConfig] = useState<PayrollCompItemConfig>({});

    const [getEmployeeData, {data: employeeData, loading: employeeLoading, error: employeeError}] = useGetPayrollEmployeeDataLazyQuery({
        fetchPolicy: "no-cache"
    });
    const [getCompItemConfig, {data: compItemConfigData, loading: compItemConfigLoading}] = useGetPayrollClientCompItemConfigurationLazyQuery({
        fetchPolicy: "no-cache"
    });

    useEffect(() => {
        if(!employeeLoading && employeeData && props.currentProperty) {

            // acquisition property dates need to be transposed to their correct display values
            if (props.currentProperty.isAcquisitionProperty) {
                for (const emp of employeeData.listEmployees.items) {
                    emp.startDate = transposeAcquisitionDateDatabaseToDisplay(emp.startDate, props.currentProperty);
                    emp.terminationDate = transposeAcquisitionDateDatabaseToDisplay(emp.terminationDate, props.currentProperty);

                    for (let i = 0; i < emp.raises.length; i++) {
                        const thisRaise = emp.raises[i];

                        if (thisRaise) {
                            thisRaise.onetimeRaiseDate = transposeAcquisitionDateDatabaseToDisplay(thisRaise.onetimeRaiseDate, props.currentProperty);
                        }
                    }

                    for (let i = 0; i < emp.bonuses.length; i++) {
                        const thisBonus = emp.bonuses[i];

                        if (thisBonus) {
                            thisBonus.onetimeBonusDate = transposeAcquisitionDateDatabaseToDisplay(thisBonus.onetimeBonusDate, props.currentProperty);
                        }
                    }

                    for (let i = 0; i < emp.overtime.length; i++) {
                        const thisOvertime = emp.overtime[i];

                        if (thisOvertime) {
                            thisOvertime.effectiveStartDate = transposeAcquisitionDateDatabaseToDisplay(thisOvertime.effectiveStartDate, props.currentProperty);
                            thisOvertime.effectiveEndDate = transposeAcquisitionDateDatabaseToDisplay(thisOvertime.effectiveEndDate, props.currentProperty);
                        }
                    }

                    for (let i = 0; i < emp.compItems.length; i++) {
                        const thisCompItem = emp.compItems[i];

                        if (thisCompItem) {
                            thisCompItem.effectiveStartDate = transposeAcquisitionDateDatabaseToDisplay(thisCompItem.effectiveStartDate, props.currentProperty);
                        }
                    }
                }
            }

            if(employeeData.listEmployees.hasMore && employeeData.listEmployees.cursor && props.budgetYear) {
                setPartialData([...partialData, ...employeeData.listEmployees.items]);
                getEmployeeData({
                    variables: {
                        cursor: employeeData.listEmployees.cursor,
                        budgetYear: props.budgetYear
                    }
                });
            } else {
                setData([...partialData, ...employeeData.listEmployees.items]);
                setLoading(false);
                setPartialData([]);
            }
        }
    }, [employeeLoading, employeeData, props.currentProperty]);

    useEffect(() => {
        if(!employeeLoading && employeeError) {
            setLoading(false);
            setError(employeeError);
        }
    }, [employeeLoading, employeeError]);

    useEffect(() => {
        if(compItemConfigLoading || !compItemConfigData) {
            return;
        }

        setCompItemConfig(compItemConfigData.getPayrollClientCompItemConfiguration);
    }, [compItemConfigData, compItemConfigLoading]);

    useEffect(() => {
        if(props.budgetYear && props.currentProperty){
            getEmployeeData({
                variables: {
                    budgetYear: props.budgetYear
                }
            });
            getCompItemConfig({
                variables: {
                    budgetYear: props.budgetYear
                }
            });
        }
    }, [props.budgetYear, props.currentProperty]);

    const refetch = () => {
        if(props.budgetYear && props.currentProperty){
            setLoading(true);
            setData(undefined);
            setError(undefined);
            setPartialData([]);
            getEmployeeData({
                variables: {
                    budgetYear: props.budgetYear
                }
            });
        }
    };

    return {
        payrollEmployeeData: data,
        payrollEmployeeDataLoading: loading || compItemConfigLoading,
        payrollEmployeeDataError: error,
        payrollEmployeeDataRefetch: refetch,
        payrollCompItemConfig: compItemConfig
    };
}
